import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import Analytics from '../components/Template/Analytics';

// Five subsystems, drawn as an exploded assembly. Every item shipped.
const PARTS = [
  {
    n: '01',
    t: 'Security & Compliance',
    d: 'PCI DSS · GRC · Qualys',
    w: 1.0,
    items: [
      ['PCI DSS practice', 'Consultant · 2023—'],
      ['Cybersecurity Portfolio', 'labs & simulations', 'https://github.com/marcusg999/CybersecurityPortfolio'],
      ['Google Cybersecurity Cert', '2024'],
    ],
  },
  {
    n: '02',
    t: 'AI & Agents',
    d: 'TypeScript · Python · LLM',
    w: 0.92,
    items: [
      ['Vesica Academy', 'live app', 'https://vesicaacademy.com/'],
      ['ArtlogixAI', 'client aggregator', 'https://artlogix-dashboard-production.up.railway.app/'],
      ['Trading Bot', 'paper-trading dashboard', 'https://github.com/marcusg999/tradingbot'],
      ['JobFlow AI', 'workflow tool', 'https://github.com/marcusg999/jobflowAI'],
    ],
  },
  {
    n: '03',
    t: 'Esoteric Software',
    d: 'Ritual · symbol · frequency',
    w: 0.84,
    items: [
      ['777 Ritual Designer', 'correspondence engine', 'https://marcusg999.github.io/777ritualdesigner/'],
      ['Sigil Generator', 'intention → 3D sigil', 'https://marcusg999.github.io/sigilgenerator.github.io/'],
      ['LBRP Guide', 'illuminated walkthrough', 'https://marcusg999.github.io/LBRPGuide.github.io/'],
      ['NeuroWave Synth', 'binaural soundscapes', 'https://marcusg999.github.io/neurowavesynth.github.io/'],
      ['Cut-Up Scrambler', 'Burroughs method', 'https://marcusg999.github.io/BurroughsCutUpScrambler.github.io/'],
      ['Seed of Samsara', 'karmic cycle visualizer', 'https://marcusg999.github.io/seedofSamsara/'],
      ['Remote View Trainer', 'psi training protocol', 'https://marcusg999.github.io/remoteviewtrainer/'],
    ],
  },
  {
    n: '04',
    t: 'Web3 & Blockchain',
    d: 'Cardano · NFT · DApp',
    w: 0.76,
    items: [
      ['Decentralize Music', 'CTO · Cardano DApp'],
      ['Jazz Is Dead NFT', 'Web3 director · vinyl subscription'],
      ['Paid in Bitcoin', '2016 — the first contract'],
    ],
  },
  {
    n: '05',
    t: 'Client Sites',
    d: 'Design · build · ship',
    w: 0.60,
    items: [
      ['Brandon Auman', 'writer / showrunner portfolio', 'https://marcusg999.github.io/abraxussite/brandon/'],
      ['Art Don’t Sleep', 'WordPress build', 'https://Artdontsleep.com'],
      ['Alkaline Luxe', 'WordPress build', 'https://alkalineluxe.com/'],
    ],
  },
  {
    n: '06',
    t: 'Film & Ventures',
    d: 'Directed · produced · owned',
    w: 0.68,
    items: [
      ['RoadKill Karma', 'short AI film', 'https://www.youtube.com/watch?v=BhWhmSGtZpg'],
      ['Physics of HipHop', 'grimoire & channel', 'https://physicsofhiphop.com/'],
      ['FitShamer', 'iPhone app', 'https://marcusg999.github.io/fitshamer.github.io/'],
      ['HipHop Diaper Bag', 'MediaPipe hand-tracking · move it by hand', 'https://hiphopdiaperbag.com/'],
      ['Client short film', 'diffusion-treated', 'https://youtu.be/u23G1ElnVjM'],
    ],
  },
];

const HISTORY = [
  ['2023 —', 'Atelier 4 · Lead Art Handling Technician',
    'Lead technician for packing, installation and interstate transport of museum-quality fine '
    + 'art, including the inaugural installation of the Lucas Museum of Narrative Art.'],
  ['2022 —', 'The Physics of Hip-Hop · Author',
    'Published author (Amazon, 2024). Own and operate the platform, channels and product line '
    + 'behind the grimoire.'],
  ['2022', '677 Nimes Rd · Construction Project Manager',
    'Managed a $40M residential build in Bel Air from conception through certificate of '
    + 'occupancy, supervising 30+ daily contractors.'],
  ['2023 —', 'PCI DSS Consultant',
    'Narrowed cardholder data environments to cut assessment fees, and updated information '
    + 'security policy for PCI DSS 4.0. Integrated PCI principles into assessment protocols, '
    + 'reducing breach likelihood by 25%.'],
  ['2021 — 23', 'Decentralize Music · CTO / PM',
    'Cut $100,000 from the build of a Cardano decentralized application. Assessed EC2 '
    + 'configuration and firewall design, and ran role-based access for contractors across a '
    + 'two-year project.'],
  ['2020 — 23', 'Jazz Is Dead · Web3 Director',
    'Led a small team designing an NFT vinyl subscription minted on Cardano, with wallet '
    + 'encryption and key-rotation policy — a 25% reduction in unauthorized-access risk.'],
  ['2016 —', 'Datum · Paid in Bitcoin',
    'Started building websites for Bitcoin, which began the blockchain work. General Assembly '
    + 'Web Development Immersive; Google Cybersecurity Certificate; Qualys VMDR.'],
];

const REVISIONS = [
  ['08', '2026', 'AI agent practice — Vesica Academy, ArtlogixAI shipped'],
  ['07', '2024', 'Google Cybersecurity Certificate issued'],
  ['06', '2023', 'Qualys VMDR · VM · CSAM certifications'],
  ['05', '2016', 'General Assembly — Web Development Immersive'],
];

// isometric plate geometry
const CX = 250;
const TOP = 70;
const GAP = 94;
const THICK = 15;
const BASE = 150;
const DIM_X = 44;

const Blueprint = () => {
  const [dark, setDark] = useState(false);
  const [hot, setHot] = useState(-1);
  const rootRef = useRef(null);
  const svgRef = useRef(null);

  // paint the drafting grid on <body> only while this page is mounted
  useEffect(() => {
    document.body.classList.add('bp-active');
    return () => {
      document.body.classList.remove('bp-active', 'bp-dark');
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('bp-dark', dark);
  }, [dark]);

  // pen-plotter draw-on for every stroke in the assembly
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const nodes = svg.querySelectorAll('polygon, line');
    nodes.forEach((node, i) => {
      let len = 0;
      try {
        len = node.getTotalLength ? node.getTotalLength() : 0;
      } catch (e) {
        len = 0;
      }
      if (!len) return;
      const el = node;
      el.style.setProperty('--len', len);
      el.style.setProperty('animation-delay', `${0.05 * i}s`);
      el.classList.add('draw');
    });
  }, []);

  // reveal sections as the sheet is read
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const targets = root.querySelectorAll('.rv');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0.12 });
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <HelmetProvider>
      <Analytics />
      <Helmet defer={false}>
        <title>Marcus Gray — Polymath, Assembled</title>
        <meta
          name="description"
          content="Marcus Gray — security consultant, AI agent builder, web3 and film. A career drawn as an exploded engineering assembly."
        />
      </Helmet>

      <div className="bp" ref={rootRef} data-sheet={dark ? 'dark' : 'light'}>
        <button className="toggle" type="button" onClick={() => setDark((v) => !v)}>
          {dark ? 'Vellum' : 'Cyanotype'}
        </button>

        <div className="sheet">
          <div className="frame">
            {['1', '2', '3', '4', '5'].map((z, i) => (
              <span className="zone t" key={z} style={{ left: `${16 + i * 17}%` }}>{z}</span>
            ))}
            {['A', 'B', 'C', 'D'].map((z, i) => (
              <span className="zone l" key={z} style={{ top: `${22 + i * 22}%` }}>{z}</span>
            ))}

            <div className="inner">
              <header>
                <p className="stamp">Drawing No. MG-001 · General Arrangement · Rev. 08</p>
                <h1 data-testid="heading">
                  Marcus Gray
                  <span>Polymath, assembled</span>
                </h1>
                <p className="sub">
                  Security consultant · AI agent builder · Web3 · Filmmaker
                  <br />
                  Los Angeles, CA · Scale 1:1 · Units — years, shipped
                </p>
                <p className="lead">
                  Six subsystems, built in sequence and now running in parallel. The assembly
                  below is exploded for clarity: hover any plate to isolate it, or read the parts
                  list. Every part is a thing that shipped.
                </p>
                <nav className="bpnav">
                  <Link to="/projects">Web sites</Link>
                  <Link to="/AI">AI Work</Link>
                  <Link to="/resume">Resume</Link>
                  <Link to="/about">About</Link>
                  <Link to="/contact">Contact</Link>
                </nav>
              </header>

              <section className="rv">
                <div className="head">
                  <span className="no">Fig. 1</span>
                  <h2>Exploded Assembly</h2>
                  <span className="sc">Isometric · not to scale</span>
                </div>
                <div className="asm">
                  <svg
                    className="asmSvg"
                    ref={svgRef}
                    viewBox="0 0 620 660"
                    role="img"
                    aria-label="Exploded isometric assembly of five career subsystems"
                  >
                    <line className="axis" x1={CX} y1={24} x2={CX} y2={TOP + GAP * (PARTS.length - 1) + 70} />
                    <line className="dim" x1={DIM_X} y1={TOP} x2={DIM_X} y2={TOP + GAP * (PARTS.length - 1)} />
                    <line className="dim" x1={DIM_X - 5} y1={TOP} x2={DIM_X + 5} y2={TOP} />
                    <line
                      className="dim"
                      x1={DIM_X - 5}
                      y1={TOP + GAP * (PARTS.length - 1)}
                      x2={DIM_X + 5}
                      y2={TOP + GAP * (PARTS.length - 1)}
                    />
                    <text
                      className="dimT"
                      x={DIM_X - 9}
                      y={TOP + (GAP * (PARTS.length - 1)) / 2}
                      textAnchor="middle"
                      transform={`rotate(-90 ${DIM_X - 9} ${TOP + (GAP * (PARTS.length - 1)) / 2})`}
                    >
                      15 YEARS BUILDING
                    </text>

                    {PARTS.map((p, i) => {
                      const y = TOP + i * GAP;
                      const a = BASE * p.w;
                      const b = a * 0.42;
                      const lx = CX + a;
                      const ly = y - b * 0.35;
                      return (
                        <g
                          className={`plate${hot === i ? ' on' : ''}`}
                          key={p.n}
                          tabIndex={0}
                          role="button"
                          aria-label={`${p.t} — part ${p.n}`}
                          onMouseEnter={() => setHot(i)}
                          onMouseLeave={() => setHot(-1)}
                          onFocus={() => setHot(i)}
                          onBlur={() => setHot(-1)}
                        >
                          <polygon
                            className="side"
                            points={`${CX - a},${y} ${CX},${y + b} ${CX},${y + b + THICK} ${CX - a},${y + THICK}`}
                          />
                          <polygon
                            className="side"
                            points={`${CX + a},${y} ${CX},${y + b} ${CX},${y + b + THICK} ${CX + a},${y + THICK}`}
                          />
                          <polygon
                            className="top"
                            points={`${CX - a},${y} ${CX},${y - b} ${CX + a},${y} ${CX},${y + b}`}
                          />
                          <line x1={lx} y1={ly} x2={lx + 34} y2={ly - 24} />
                          <line x1={lx + 34} y1={ly - 24} x2={lx + 96} y2={ly - 24} />
                          <text className="pn" x={lx + 38} y={ly - 41}>{p.n}</text>
                          <text x={lx + 38} y={ly - 29}>{p.t.toUpperCase()}</text>
                        </g>
                      );
                    })}
                  </svg>

                  <div className="bom">
                    <h3><span>Parts List</span><span>{`Qty ${PARTS.length}`}</span></h3>
                    {PARTS.map((p, i) => (
                      <button
                        type="button"
                        className={`bomRow${hot === i ? ' on' : ''}`}
                        key={p.n}
                        onMouseEnter={() => setHot(i)}
                        onMouseLeave={() => setHot(-1)}
                        onFocus={() => setHot(i)}
                        onBlur={() => setHot(-1)}
                      >
                        <span className="n">{p.n}</span>
                        <span>
                          <span className="t">{p.t}</span>
                          <span className="d">{p.d}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <section className="rv">
                <div className="head">
                  <span className="no">Fig. 2</span>
                  <h2>Detail Views</h2>
                  <span className="sc">Sections A–E · components</span>
                </div>
                <div className="details">
                  {PARTS.map((p, i) => (
                    <div className="det" key={p.n}>
                      <div className="dn">
                        {`Detail ${String.fromCharCode(65 + i)} · Part ${p.n}`}
                      </div>
                      <h3>{p.t}</h3>
                      <ul>
                        {p.items.map((it) => (
                          <li key={it[0]}>
                            {it[2] ? (
                              <a href={it[2]} target="_blank" rel="noopener noreferrer">
                                <span>{it[0]}</span>
                                <i>{it[1]}</i>
                              </a>
                            ) : (
                              <span className="noLink">
                                <span>{it[0]}</span>
                                <i>{it[1]}</i>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rv">
                <div className="head">
                  <span className="no">Fig. 3</span>
                  <h2>Elevation · Build History</h2>
                  <span className="sc">Vertical datum — 2016</span>
                </div>
                <div className="elev">
                  {HISTORY.map((h) => (
                    <React.Fragment key={h[1]}>
                      <div className="yr">{h[0]}</div>
                      <div className="bd">
                        <h3>{h[1]}</h3>
                        <p>{h[2]}</p>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </section>

              <section className="rv">
                <div className="head">
                  <span className="no">Fig. 4</span>
                  <h2>Revision History</h2>
                  <span className="sc">Certifications &amp; marks</span>
                </div>
                <table className="revs">
                  <thead>
                    <tr><th>Rev</th><th>Date</th><th>Description</th><th>By</th></tr>
                  </thead>
                  <tbody>
                    {REVISIONS.map((r) => (
                      <tr key={r[0]}>
                        <td>{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td><td>MG</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <div className="tblock rv">
                <div className="cell">
                  <div className="k">Drawn by</div>
                  <div className="v">Marcus Gray</div>
                  <div className="k" style={{ marginTop: '12px' }}>Contact</div>
                  <div className="v sm">
                    <a href="mailto:marcusgraydev@gmail.com">marcusgraydev@gmail.com</a>
                  </div>
                </div>
                <div className="cell">
                  <div className="k">Repositories</div>
                  <div className="v sm">
                    <a href="https://github.com/marcusg999" target="_blank" rel="noopener noreferrer">
                      github.com/marcusg999
                    </a>
                  </div>
                  <div className="k" style={{ marginTop: '12px' }}>Profile</div>
                  <div className="v sm">
                    <a href="https://linkedin.com/in/marcusgraydev" target="_blank" rel="noopener noreferrer">
                      linkedin.com/in/marcusgraydev
                    </a>
                  </div>
                </div>
                <div className="cell">
                  <div className="k">Sheet</div>
                  <div className="v">1 of 1</div>
                  <div className="k" style={{ marginTop: '12px' }}>Location</div>
                  <div className="v">Los Angeles</div>
                </div>
              </div>
            </div>
          </div>

          <p className="foot">
            Sheet MG-001 · All dimensions in years unless noted · Do not scale from drawing
          </p>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Blueprint;
